<template>
    <div class="offer-item d--flex width-100% justify-between">
        <div v-if="versionOrVehicle" class="offer-item__version">
            <div class="offer-item__version-headline d--flex items-center">
                <div class="d--flex items-center">
                    <span
                        v-if="versionOrVehicle.efficiencyClass"
                        :class="[
                            'offer-item__version-efficiency',
                            'efficiency-label',
                            `efficiency-label--${versionOrVehicle.efficiencyClass.label}`,
                            'm-right--xs',
                        ]"
                    >
                        {{ versionOrVehicle.efficiencyClass.value }}
                    </span>
                    <h4
                        v-if="versionOrVehicle.name"
                        class="offers-item__version-name ts--caption ts--uppercase ts-weight--700"
                    >
                        {{ versionOrVehicle.name }}
                    </h4>
                </div>
                <div
                    class="offer-item__version-type p-top--xxs p-bottom--xxs p-left--xs p-right--xs br-radius border--grey-200 ts--uppercase ts--micro ts-weight--700 m-left--xxs m-right--xxs"
                >
                    {{ badge }}
                </div>
            </div>
            <div class="offer-item__version-details d--flex items-center m-top--xxs">
                <span
                    v-if="item.type && item.type.toLowerCase() === 'offering'"
                    class="ts--small color--grey-700 ts-weight--700"
                >
                    {{ item.label }}&nbsp;
                    <del v-if="item.originalPrice">
                        {{ item.originalPrice }}
                    </del>
                </span>
                <div v-else-if="versionOrVehicle.details && versionOrVehicle.details.length">
                    <span
                        v-for="(detail, i) in versionOrVehicle.details"
                        :key="i"
                        class="offer-item__version-details-item ts--small color--grey-700 ts-weight--700"
                    >
                        <span v-if="detail.label">
                            {{ detail.label }}
                        </span>
                        <span :class="detail.label && isStockOffer && i === 0 && 'ts--line-through'">
                            {{ detail.value }}
                        </span>
                    </span>
                </div>
                <div
                    v-if="versionOrVehicle.disclaimer"
                    class="offer-item__version-details-dislaimer-icon d--flex items-center m-left--xxs"
                    @click="openDisclaimerModal"
                >
                    <icon name="info-circle" size="xs" color="primary" />
                </div>
            </div>
            <div class="m-top--xxs">
                <span
                    v-if="versionOrVehicle.emission"
                    class="offer-item__version-legal-compliance ts--micro color--grey-700"
                >
                    {{ versionOrVehicle.emission.label }}&nbsp;{{ versionOrVehicle.emission.value }}
                </span>
                <span
                    v-if="versionOrVehicle.consumption"
                    class="offer-item__version-legal-compliance ts--micro color--grey-700"
                >
                    {{ versionOrVehicle.consumption.label }}&nbsp;{{ versionOrVehicle.consumption.value }}
                </span>
                <template v-if="versionOrVehicle.techSpecs">
                    <span
                        v-for="(techSpec, i) in versionOrVehicle.techSpecs"
                        :key="`vehicle-tech-spec-${i}`"
                        class="offer-item__version-legal-compliance ts--micro color--grey-700"
                    >
                        {{ techSpec.label }}&nbsp;{{ techSpec.value }}
                    </span>
                </template>
            </div>
        </div>
        <div class="offer-item__price ts--body-small">
            <a
                v-if="!isStockOffer"
                :href="item.href"
                target="_blank"
                rel="nofollow"
                class="ts-link ts-link--primary icon-text"
                @click="trackVehiclePricingClick()"
            >
                <span>
                    {{ item.finalPrice }}
                    <small v-if="item.priceSuffix" class="ts--micro">
                        {{ item.priceSuffix }}
                    </small>
                </span>
                <icon name="generic-chevron-right" size="xxs" color="primary" />
            </a>
            <button v-else class="ts-link ts-link--primary icon-text" @click="handlePriceClick">
                <span>
                    {{ item.finalPrice }}
                    <small v-if="item.priceSuffix" class="ts--micro">
                        {{ item.priceSuffix }}
                    </small>
                </span>
                <icon name="generic-chevron-right" size="xxs" color="primary" />
            </button>
        </div>
    </div>
</template>

<script>
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import { dkModalAware } from '@v4/common/components/mixins/modal';

/**
 * Search page vehicle card offer items
 *
 * @version 4.0.0
 */
export default {
    name: 'OfferItem',
    components: {
        Icon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/Icon.vue'),
        }),
    },
    mixins: [dkModalAware, gaEventHandler],
    props: {
        /**
         * Offer info object.
         */
        item: {
            type: Object,
            default: () => ({}),
        },
        isStockOffer: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        versionOrVehicle() {
            return this.item.version ? this.item.version : this.item.vehicle;
        },
        badge() {
            return this.item.badgeOfferType ? this.item.badgeOfferType : this.item.label;
        },
    },
    methods: {
        trackVehiclePricingClick() {
            this.pushGaEvent('Search page', 'vehicle pricing click', `Search page card - ${this.item.type} price`);
            this.$emit('click-select-promotion');
        },
        openDisclaimerModal() {
            return import(
                /* webpackChunkName: "DisclaimerModal-vue" */ '@v4/common/components/atoms/DisclaimerModal'
            ).then(
                async ({ default: DisclaimerModal }) =>
                    await this.showModal(
                        DisclaimerModal,
                        {
                            disclaimer: this.versionOrVehicle.disclaimer,
                        },
                        {
                            name: 'disclaimer-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            width: 515,
                            scrollable: true,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
        handlePriceClick(e) {
            e.preventDefault();
            this.$emit('price-clicked');
        },
    },
};
</script>

<style lang="scss" src="@v4/common/components/molecules/VehicleCard/components/molecules/OfferItem/_index.scss"></style>
